/**
 * PhotoDeleteDialog — confirmation gate before permanently erasing a photo.
 *
 * This is a destructive, irreversible operation. The dialog copy makes
 * that unmistakably clear:
 *   - "permanently erases the photo everywhere"
 *   - "including any shared links"
 *   - "this cannot be undone"
 *
 * The confirm button uses variant="danger". Cancel is the default focus.
 * Escape key closes (cancels).
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import { useDeletePhoto } from '@/hooks/usePhotoActions';
import { AlertTriangle } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';

export interface PhotoDeleteDialogProps {
  photoId: string;
  vehicleId: string;
  onClose: () => void;
  /** Called after the DELETE 204 — parent removes the tile. */
  onDeleted: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.88)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: SPACE.md,
};

const dialogStyle: CSSProperties = {
  background: T.bg,
  borderRadius: RADIUS.lg,
  width: '100%',
  maxWidth: 420,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const bodyStyle: CSSProperties = {
  padding: SPACE.lg,
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.md,
};

const iconRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
  padding: `0 ${SPACE.lg}px ${SPACE.lg}px`,
  justifyContent: 'flex-end',
};

const separatorStyle: CSSProperties = {
  height: 1,
  background: T.border,
  margin: `0 ${SPACE.lg}px`,
};

const closeBtnStyle: CSSProperties = {
  display: 'none',
  // Intentionally hidden — Cancel button in footer serves as close.
  // Dialog is only closeable via Cancel button or Escape key.
};
void closeBtnStyle; // suppress unused var lint

export function PhotoDeleteDialog({
  photoId,
  vehicleId,
  onClose,
  onDeleted,
}: PhotoDeleteDialogProps) {
  // Focus the Cancel button first — makes the safe action the default.
  const { dialogRef, initialRef: cancelRef } = useModalFocus<HTMLDivElement, HTMLButtonElement>(
    true,
  );
  const deleteMutation = useDeletePhoto(vehicleId);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleDelete() {
    deleteMutation.mutate(photoId, { onSuccess: onDeleted });
  }

  return (
    <div style={overlayStyle} role="presentation">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-label="Permanently delete photo"
        aria-describedby="delete-dialog-desc"
        style={dialogStyle}
      >
        <div style={bodyStyle}>
          <div style={iconRowStyle}>
            <AlertTriangle size={20} aria-hidden="true" style={{ color: T.red, flexShrink: 0 }} />
            <p
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                fontFamily: F.body,
                color: T.text,
              }}
            >
              Delete photo permanently
            </p>
          </div>

          <p
            id="delete-dialog-desc"
            style={{
              margin: 0,
              fontSize: 14,
              fontFamily: F.body,
              color: T.text,
              lineHeight: 1.6,
            }}
          >
            This permanently erases the photo everywhere, including any shared links. There is no
            way to undo this.
          </p>

          {deleteMutation.isError ? (
            <p role="alert" style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.red }}>
              Something went wrong. Try again.
            </p>
          ) : null}
        </div>

        <div style={separatorStyle} aria-hidden="true" />

        <div style={footerStyle}>
          <Button
            ref={cancelRef}
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            Delete permanently
          </Button>
        </div>
      </div>
    </div>
  );
}
